import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { config } from './config';
import { getSharedVideo, unshareVideo } from './shared-library';
import {
  editSharedVideo,
  setSharedThumbnail,
  setSharedCaption,
  generateSharedCaptions,
  refreshSharedCaptions,
} from './api';
import { HlsPlayer } from './hls-player';
import {
  ShareAndEmbedCard,
  EditableTitle,
  EditableDescription,
  Thumbnail,
  CaptionStatusBadge,
} from './ui';
import { ThumbnailPicker } from './thumbnail-picker';
import { CaptionPicker } from './caption-picker';

// How often to advance an in-flight caption generation. Audio extraction takes a few minutes, so
// there's nothing to gain from polling faster.
const CAPTION_POLL_MS = 10_000;

export function SharedVideoPage() {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const [pickingThumbnail, setPickingThumbnail] = useState(false);
  const [pickingCaption, setPickingCaption] = useState(false);

  const query = useQuery({
    queryKey: ['shared-video', videoId],
    enabled: !!videoId,
    queryFn: () => getSharedVideo(videoId!),
  });

  const unshareMutation = useMutation({
    mutationFn: () => unshareVideo(videoId!),
    onSuccess: () => navigate('/shared'),
  });

  const generateMutation = useMutation({
    mutationFn: () => generateSharedCaptions(videoId!),
    // A 409 means someone else got there first; either way the item now says what's happening.
    onSettled: () => query.refetch(),
  });

  // Generation runs server-side in two stages and only moves forward when something asks it to,
  // so anyone with this page open while it's `generating` drives it along — the same way an
  // owner's Refresh drives their own upload's captions. Concurrent pollers are safe (the server
  // side is idempotent per attempt).
  const captionStatus = query.data?.captionStatus;
  useEffect(() => {
    if (!videoId || captionStatus !== 'generating') return;
    let cancelled = false;
    const tick = async () => {
      try {
        await refreshSharedCaptions(videoId);
      } catch {
        // Keep polling; a transient failure shouldn't strand the page on a stale status.
      }
      if (!cancelled) query.refetch();
    };
    tick();
    const id = setInterval(tick, CAPTION_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, captionStatus]);

  if (query.isLoading) return <p className="muted">Loading…</p>;
  if (!query.data) {
    return (
      <div>
        <p className="error">This video isn't in the shared library.</p>
        <Link to="/shared">Back to shared library</Link>
      </div>
    );
  }

  const row = query.data;
  const watchUrl = `https://${config.spaDomain}/watch/${row.videoId}`;
  const embedUrl = `https://${config.spaDomain}/embed/${row.videoId}`;
  // Generation here works from the HLS rendition, so no shared video is ever truly "unsupported";
  // that status only reaches this page mirrored from an owner's upload of an unreadable format.
  const shownCaptionStatus = row.captionStatus === 'unsupported' ? 'none' : row.captionStatus;
  const canGenerate = !shownCaptionStatus || shownCaptionStatus === 'none' || shownCaptionStatus === 'failed';

  return (
    <div>
      <Link to="/shared" className="muted">
        ← Back to shared library
      </Link>
      <EditableTitle
        title={row.title}
        onSave={async (next) => {
          await editSharedVideo(row.videoId, { title: next });
          query.refetch();
        }}
      />
      <HlsPlayer
        src={row.hlsUrl}
        poster={row.thumbnailUrl}
        captionUrl={row.captionStatus === 'ready' ? row.captionUrl : undefined}
      />
      {pickingThumbnail ? (
        <ThumbnailPicker
          subjectId={row.videoId}
          hlsUrl={row.hlsUrl}
          onSave={async (url) => {
            await setSharedThumbnail(row.videoId, url);
            setPickingThumbnail(false);
            query.refetch();
          }}
          onCancel={() => setPickingThumbnail(false)}
        />
      ) : (
        <div className="card" style={{ marginTop: 'var(--space-4)' }}>
          <h2>Thumbnail</h2>
          <Thumbnail src={row.thumbnailUrl} alt={`${row.title} thumbnail`} size="lg" />
          <div style={{ marginTop: 'var(--space-3)' }}>
            <button className="secondary" onClick={() => setPickingThumbnail(true)}>
              Choose thumbnail
            </button>
          </div>
        </div>
      )}
      {pickingCaption ? (
        <CaptionPicker
          subjectId={row.videoId}
          onSave={async (url) => {
            await setSharedCaption(row.videoId, url);
            setPickingCaption(false);
            query.refetch();
          }}
          onCancel={() => setPickingCaption(false)}
        />
      ) : (
        <div className="card" style={{ marginTop: 'var(--space-4)' }}>
          <h2>Closed captions</h2>
          <CaptionStatusBadge status={shownCaptionStatus} />
          {shownCaptionStatus === 'generating' && (
            <p className="muted">
              This takes a few minutes. You can leave this page — generation continues, and this
              status updates whenever someone has the page open.
            </p>
          )}
          <div style={{ marginTop: 'var(--space-3)', display: 'flex', gap: 8 }}>
            {canGenerate && (
              <button disabled={generateMutation.isPending} onClick={() => generateMutation.mutate()}>
                {generateMutation.isPending
                  ? 'Starting…'
                  : shownCaptionStatus === 'failed'
                    ? 'Try generating again'
                    : 'Generate captions'}
              </button>
            )}
            <button className="secondary" onClick={() => setPickingCaption(true)}>
              {shownCaptionStatus === 'ready' ? 'Replace captions' : 'Upload captions'}
            </button>
          </div>
          {generateMutation.isError && (
            <p className="error">Couldn't start caption generation — try again.</p>
          )}
        </div>
      )}
      <ShareAndEmbedCard watchUrl={watchUrl} embedUrl={embedUrl} width={row.width} height={row.height} />
      <EditableDescription
        description={row.description}
        onSave={async (next) => {
          await editSharedVideo(row.videoId, { description: next });
          query.refetch();
        }}
      />
      <div className="card" style={{ marginTop: 'var(--space-4)' }}>
        <h2>Unshare</h2>
        <p className="muted">
          Removes this video from the shared library. Anyone can unshare any item — this doesn't
          delete the underlying video.
        </p>
        <button
          className="secondary"
          disabled={unshareMutation.isPending}
          onClick={() => {
            if (confirm('Unshare this video from the shared library?')) unshareMutation.mutate();
          }}
        >
          {unshareMutation.isPending ? 'Unsharing…' : 'Unshare'}
        </button>
        {unshareMutation.isError && <p className="error">Couldn't unshare — try again.</p>}
      </div>
    </div>
  );
}
